import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Chip,
  IconButton, Tooltip, Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TuneIcon from '@mui/icons-material/Tune';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useSnackbar } from 'notistack';
import * as eqpToolLifeApi from '../../lib/api/eqpToolLife';
import {
  getToolLevelLabel, getToolLevelChipColor, getToolLevelColor,
  clampRatioPct, formatRatioPct, formatMinNumber, formatDateTime, formatLimitText, sortToolRows,
} from './eqpToolLifeFormat';
import EqpToolLifeDialog from './EqpToolLifeDialog';
import EqpToolResetDialog from './EqpToolResetDialog';

/**
 * 매거진 탭 우측 - 공구별 사용·교체 시기 표. 기준 설정/교체함은 이 컴포넌트가 다이얼로그까지 들고
 * 처리하고, 성공하면 onRefresh로 부모(EqpMagazineTab)에게 usage 재조회를 부탁한다.
 */
const EqpToolLifeTable = ({ eqpUuid, tools, onRefresh, canEdit = true }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [lifeDialogTool, setLifeDialogTool] = useState(null);
  const [resetDialogTool, setResetDialogTool] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const rows = sortToolRows(tools);

  const handleLifeSubmit = async (body) => {
    setSubmitting(true);
    try {
      await eqpToolLifeApi.setToolLife(eqpUuid, lifeDialogTool.toolNo, body);
      enqueueSnackbar(`T${lifeDialogTool.toolNo} 교체 기준을 저장했습니다.`, { variant: 'success' });
      setLifeDialogTool(null);
      onRefresh?.();
    } catch (e) {
      enqueueSnackbar('기준 저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetConfirm = async (body) => {
    setSubmitting(true);
    try {
      await eqpToolLifeApi.resetTool(eqpUuid, resetDialogTool.toolNo, body);
      enqueueSnackbar(`T${resetDialogTool.toolNo}을(를) 교체 처리했습니다.`, { variant: 'success' });
      setResetDialogTool(null);
      onRefresh?.();
    } catch (e) {
      enqueueSnackbar('교체 처리에 실패했습니다.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (rows.length === 0) {
    return <Typography variant="body2" color="text.secondary">공구 사용 기록이 아직 없습니다.</Typography>;
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell>공구</TableCell>
              <TableCell align="right">가공(분)</TableCell>
              <TableCell align="right">장착 횟수</TableCell>
              <TableCell>기준 대비</TableCell>
              <TableCell>상태 · 마지막 교체</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t) => {
              const lastReset = formatDateTime(t.lastResetDt);
              return (
                <TableRow key={t.toolNo} selected={!!t.current}>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>T{t.toolNo}</Typography>
                      {t.current && <Chip size="small" color="primary" variant="outlined" label="현재" />}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatMinNumber(t.usedMin)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {t.mountCount ?? '-'}
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flexGrow: 1, minWidth: 60 }}>
                        <LinearProgress
                          variant="determinate"
                          value={clampRatioPct(t.ratio)}
                          sx={{
                            height: 6, borderRadius: 3,
                            bgcolor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': { bgcolor: getToolLevelColor(theme, t.level) },
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                        {formatRatioPct(t.ratio)} {formatLimitText(t.limitMin)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25} alignItems="flex-start">
                      <Chip size="small" color={getToolLevelChipColor(t.level)} label={getToolLevelLabel(t.level)} />
                      {lastReset && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {lastReset} 교체
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {/* 기준 설정(한도 변경)은 "편집"이라 canEdit로 가린다. 교체함은 현장에서 공구를
                        실제로 바꾼 뒤 누르는 기록 버튼이라 보기 모드/권한 없을 때도 항상 보인다
                        (장비 상세창 결정사항: view 모드에서도 교체함은 유지). */}
                    {canEdit && (
                      <Tooltip title="기준 설정">
                        <IconButton size="small" onClick={() => setLifeDialogTool(t)}>
                          <TuneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="교체함">
                      <IconButton size="small" onClick={() => setResetDialogTool(t)}>
                        <AutorenewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <EqpToolLifeDialog
        open={!!lifeDialogTool}
        tool={lifeDialogTool}
        submitting={submitting}
        onClose={() => setLifeDialogTool(null)}
        onSubmit={handleLifeSubmit}
      />
      <EqpToolResetDialog
        open={!!resetDialogTool}
        tool={resetDialogTool}
        submitting={submitting}
        onClose={() => setResetDialogTool(null)}
        onConfirm={handleResetConfirm}
      />
    </Box>
  );
};

export default EqpToolLifeTable;
